// Holographic globe (docs/ART-DIRECTION.md §1, §5): dark body, land dot-matrix from LAND_MASK, neon
// coastline + border lines, a fresnel atmosphere and a slowly scrolling lat/long grid. Colours and brightness are the
// globe look (globeLook.ts, from the presentation tuning); setLook() retunes them in place.
//
// Draw calls: base layer = body, atmosphere, grid (3); emissive layer = depth-only occluder, land dots,
// coastlines, borders (4). Triangles: the sphere geometry is drawn three times (body, atmosphere,
// occluder), 2·w·(h−1) each for the tier's segments.

import {
  AdditiveBlending,
  BufferAttribute,
  BufferGeometry,
  Color,
  Group,
  LineBasicMaterial,
  LineSegments,
  Mesh,
  MeshBasicMaterial,
  Points,
  ShaderMaterial,
  SphereGeometry,
} from 'three'
import { LAND_MASK } from '@taxwar/content'
import { InvariantViolation } from '@taxwar/net'
import { BASE_LAYER, EMISSIVE_LAYER, assignLayer } from '../layers'
import type { QualityProfile } from '../visualSettings'
import { DEG_TO_RAD, GLOBE_ALTITUDE, GLOBE_RADIUS, writeLatLon } from '../geo/globeSpace'
import type { GlobeGeo, PolylineSet } from '../geo/polylines'
import type { GlobeLook } from './globeLook'
import {
  ATMOSPHERE_FRAGMENT,
  BODY_FRAGMENT,
  FACING_VERTEX,
  LAND_DOTS_FRAGMENT,
  LAND_DOTS_VERTEX,
} from './globeShaders'

/** ART-DIRECTION §5 globe budget. */
const MAX_LAND_DOTS = 60_000
/** Dot spacing at density 1 (High); lower tiers widen it by 1/√density. */
const BASE_DOT_STRIDE_DEG = 0.5
/** Dot diameter as a fraction of dot spacing. */
const DOT_FILL = 0.45
const MIN_DOT_PX = 1
/** Facing (cos of view angle) over which dots fade out towards the limb. */
const LAND_LIMB_FADE = 0.25

const BODY_DEPTH_POWER = 1.5
const ATMOSPHERE_SCALE = 1.035
const ATMOSPHERE_RIM_POWER = 3
const ATMOSPHERE_OUTER_FADE = 0.12

const GRID_STEP_DEG = 15
const GRID_SEGMENT_DEG = 3
const GRID_SCROLL_RAD_PER_SEC = 0.01
const FULL_TURN = Math.PI * 2

const POSITION_COMPONENTS = 3
const SEGMENT_VERTICES = 2

export class GlobeRenderer {
  readonly root = new Group()

  private sphereGeometry: SphereGeometry
  private landGeometry: BufferGeometry
  private readonly gridGeometry: BufferGeometry
  private readonly coastlineGeometry: BufferGeometry
  private readonly borderGeometry: BufferGeometry

  private readonly bodyMaterial: ShaderMaterial
  private readonly atmosphereMaterial: ShaderMaterial
  private readonly occluderMaterial: MeshBasicMaterial
  private readonly landMaterial: ShaderMaterial
  private readonly gridMaterial: LineBasicMaterial
  private readonly coastlineMaterial: LineBasicMaterial
  private readonly borderMaterial: LineBasicMaterial

  private readonly body: Mesh
  private readonly atmosphere: Mesh
  private readonly occluder: Mesh
  private readonly land: Points
  private readonly grid: LineSegments

  private readonly dotSize = { value: 0 }
  private readonly pointScale = { value: 1 }
  /** Uniform values setLook() writes; the materials hold them by reference. */
  private readonly limbColour = new Color()
  private readonly coreColour = new Color()
  private readonly atmosphereColour = new Color()
  private readonly atmosphereIntensity = { value: 0 }
  private readonly landColour = new Color()
  private readonly landIntensity = { value: 0 }
  private gridAngle = 0

  constructor(geo: GlobeGeo, profile: QualityProfile, look: GlobeLook) {
    this.sphereGeometry = buildSphere(profile)
    this.landGeometry = buildLandDots(dotStrideDeg(profile))
    this.dotSize.value = dotDiameter(profile)
    this.gridGeometry = buildGrid()
    this.coastlineGeometry = buildPolylineGeometry(geo.coastlines, GLOBE_ALTITUDE.coastline)
    this.borderGeometry = buildPolylineGeometry(geo.borders, GLOBE_ALTITUDE.borders)

    this.bodyMaterial = new ShaderMaterial({
      uniforms: {
        uLimbColor: { value: this.limbColour },
        uCoreColor: { value: this.coreColour },
        uDepthPower: { value: BODY_DEPTH_POWER },
      },
      vertexShader: FACING_VERTEX,
      fragmentShader: BODY_FRAGMENT,
    })
    this.atmosphereMaterial = new ShaderMaterial({
      uniforms: {
        uColor: { value: this.atmosphereColour },
        uIntensity: this.atmosphereIntensity,
        uRimPower: { value: ATMOSPHERE_RIM_POWER },
        uOuterFade: { value: ATMOSPHERE_OUTER_FADE },
      },
      vertexShader: FACING_VERTEX,
      fragmentShader: ATMOSPHERE_FRAGMENT,
      transparent: true,
      blending: AdditiveBlending,
      depthWrite: false,
    })
    // Depth-only stand-in for the body in the emissive pass, so the far side of the globe stays hidden.
    this.occluderMaterial = new MeshBasicMaterial({ colorWrite: false })
    this.landMaterial = new ShaderMaterial({
      uniforms: {
        uColor: { value: this.landColour },
        uIntensity: this.landIntensity,
        uLimbFade: { value: LAND_LIMB_FADE },
        uDotSize: this.dotSize,
        uPointScale: this.pointScale,
        uMinPointPx: { value: MIN_DOT_PX },
      },
      vertexShader: LAND_DOTS_VERTEX,
      fragmentShader: LAND_DOTS_FRAGMENT,
      transparent: true,
      blending: AdditiveBlending,
      depthWrite: false,
    })
    this.gridMaterial = additiveLineMaterial()
    this.coastlineMaterial = additiveLineMaterial()
    this.borderMaterial = additiveLineMaterial()
    this.setLook(look)

    this.body = new Mesh(this.sphereGeometry, this.bodyMaterial)
    this.atmosphere = new Mesh(this.sphereGeometry, this.atmosphereMaterial)
    this.atmosphere.scale.setScalar(ATMOSPHERE_SCALE)
    this.occluder = new Mesh(this.sphereGeometry, this.occluderMaterial)
    this.grid = new LineSegments(this.gridGeometry, this.gridMaterial)
    this.land = new Points(this.landGeometry, this.landMaterial)
    const coastlines = new LineSegments(this.coastlineGeometry, this.coastlineMaterial)
    const borders = new LineSegments(this.borderGeometry, this.borderMaterial)

    for (const object of [this.body, this.atmosphere, this.grid]) assignLayer(object, BASE_LAYER)
    for (const object of [this.occluder, this.land, borders, coastlines]) assignLayer(object, EMISSIVE_LAYER)
    this.root.add(this.body, this.atmosphere, this.grid, this.occluder, this.land, borders, coastlines)
  }

  /** Colours and brightness from the globe look (the tuner retunes a running globe through here). */
  setLook(look: GlobeLook): void {
    const { palette, brightness } = look
    this.limbColour.copy(palette.background)
    this.coreColour.copy(palette.globeDepth)
    this.atmosphereColour.copy(palette.atmosphere)
    this.atmosphereIntensity.value = brightness.atmosphere
    this.landColour.copy(palette.landDots)
    this.landIntensity.value = brightness.landDots
    paintLines(this.gridMaterial, palette.grid, brightness.grid)
    paintLines(this.coastlineMaterial, palette.coastline, brightness.coastline)
    paintLines(this.borderMaterial, palette.borders, brightness.borders)
  }

  /** Rebuilds the quality-dependent geometry (sphere tessellation, land dot density). */
  setQuality(profile: QualityProfile): void {
    const sphere = buildSphere(profile)
    const land = buildLandDots(dotStrideDeg(profile))
    this.sphereGeometry.dispose()
    this.landGeometry.dispose()
    this.sphereGeometry = sphere
    this.landGeometry = land
    this.body.geometry = sphere
    this.atmosphere.geometry = sphere
    this.occluder.geometry = sphere
    this.land.geometry = land
    this.dotSize.value = dotDiameter(profile)
  }

  /** Device pixels per render unit at unit view depth (see projectionScale in the camera rig). */
  setProjectionScale(pixelsPerUnit: number): void {
    this.pointScale.value = pixelsPerUnit
  }

  update(dtSeconds: number): void {
    this.gridAngle = (this.gridAngle + dtSeconds * GRID_SCROLL_RAD_PER_SEC) % FULL_TURN
    this.grid.rotation.y = this.gridAngle
  }

  dispose(): void {
    this.root.removeFromParent()
    this.sphereGeometry.dispose()
    this.landGeometry.dispose()
    this.gridGeometry.dispose()
    this.coastlineGeometry.dispose()
    this.borderGeometry.dispose()
    this.bodyMaterial.dispose()
    this.atmosphereMaterial.dispose()
    this.occluderMaterial.dispose()
    this.landMaterial.dispose()
    this.gridMaterial.dispose()
    this.coastlineMaterial.dispose()
    this.borderMaterial.dispose()
  }
}

function buildSphere(profile: QualityProfile): SphereGeometry {
  return new SphereGeometry(GLOBE_RADIUS, profile.sphereWidthSegments, profile.sphereHeightSegments)
}

function dotStrideDeg(profile: QualityProfile): number {
  return BASE_DOT_STRIDE_DEG / Math.sqrt(profile.landPointDensity)
}

function dotDiameter(profile: QualityProfile): number {
  return dotStrideDeg(profile) * DEG_TO_RAD * GLOBE_ALTITUDE.landDots * DOT_FILL
}

/**
 * Visits a near-equal-area dot lattice: rows of constant latitude, each with a column count proportional
 * to cos(lat), odd rows staggered by half a column for a hex-like matrix.
 */
function visitDotLattice(strideDeg: number, visit: (latDeg: number, lonDeg: number) => void): void {
  const rows = Math.round(180 / strideDeg)
  const rowDeg = 180 / rows
  for (let row = 0; row < rows; row++) {
    const lat = -90 + (row + 0.5) * rowDeg
    const columns = Math.max(1, Math.round((360 * Math.cos(lat * DEG_TO_RAD)) / rowDeg))
    const columnDeg = 360 / columns
    const stagger = (row & 1) * 0.5 * columnDeg
    for (let column = 0; column < columns; column++) visit(lat, -180 + (column + 0.5) * columnDeg + stagger)
  }
}

function buildLandDots(strideDeg: number): BufferGeometry {
  let count = 0
  visitDotLattice(strideDeg, (lat, lon) => {
    if (LAND_MASK.isLand(lat, lon)) count++
  })
  if (count > MAX_LAND_DOTS) {
    throw new InvariantViolation(`land dot cloud has ${count} points at stride ${strideDeg}°, budget ${MAX_LAND_DOTS}`)
  }
  const positions = new Float32Array(count * POSITION_COMPONENTS)
  let written = 0
  visitDotLattice(strideDeg, (lat, lon) => {
    if (!LAND_MASK.isLand(lat, lon)) return
    writeLatLon(positions, written * POSITION_COMPONENTS, lat, lon, GLOBE_ALTITUDE.landDots)
    written++
  })
  const geometry = new BufferGeometry()
  geometry.setAttribute('position', new BufferAttribute(positions, POSITION_COMPONENTS))
  return geometry
}

/** Latitude rings (poles excluded) and meridians, as LineSegments pairs. */
function buildGrid(): BufferGeometry {
  const latLines = 180 / GRID_STEP_DEG - 1
  const lonLines = 360 / GRID_STEP_DEG
  const ringSegments = 360 / GRID_SEGMENT_DEG
  const meridianSegments = 180 / GRID_SEGMENT_DEG
  const segments = latLines * ringSegments + lonLines * meridianSegments
  const positions = new Float32Array(segments * SEGMENT_VERTICES * POSITION_COMPONENTS)
  const radius = GLOBE_ALTITUDE.grid
  let vertex = 0
  const pushSegment = (lat0: number, lon0: number, lat1: number, lon1: number): void => {
    writeLatLon(positions, vertex * POSITION_COMPONENTS, lat0, lon0, radius)
    writeLatLon(positions, (vertex + 1) * POSITION_COMPONENTS, lat1, lon1, radius)
    vertex += SEGMENT_VERTICES
  }
  for (let i = 1; i <= latLines; i++) {
    const lat = -90 + i * GRID_STEP_DEG
    for (let s = 0; s < ringSegments; s++) pushSegment(lat, s * GRID_SEGMENT_DEG, lat, (s + 1) * GRID_SEGMENT_DEG)
  }
  for (let i = 0; i < lonLines; i++) {
    const lon = -180 + i * GRID_STEP_DEG
    for (let s = 0; s < meridianSegments; s++) {
      pushSegment(-90 + s * GRID_SEGMENT_DEG, lon, -90 + (s + 1) * GRID_SEGMENT_DEG, lon)
    }
  }
  const geometry = new BufferGeometry()
  geometry.setAttribute('position', new BufferAttribute(positions, POSITION_COMPONENTS))
  return geometry
}

/** One indexed LineSegments geometry per polyline set: every polyline becomes consecutive point pairs. */
function buildPolylineGeometry(set: PolylineSet, radius: number): BufferGeometry {
  const positions = new Float32Array(set.totalPoints * POSITION_COMPONENTS)
  for (let p = 0; p < set.totalPoints; p++) {
    const lon = set.lonLat[p * 2] as number
    const lat = set.lonLat[p * 2 + 1] as number
    writeLatLon(positions, p * POSITION_COMPONENTS, lat, lon, radius)
  }
  const indices = new Uint32Array(set.totalSegments * SEGMENT_VERTICES)
  let start = 0
  let cursor = 0
  for (const count of set.pointCounts) {
    for (let k = 0; k < count - 1; k++) {
      indices[cursor++] = start + k
      indices[cursor++] = start + k + 1
    }
    start += count
  }
  const geometry = new BufferGeometry()
  geometry.setAttribute('position', new BufferAttribute(positions, POSITION_COMPONENTS))
  geometry.setIndex(new BufferAttribute(indices, 1))
  return geometry
}

/** An additive line material; setLook() paints it (paintLines). */
function additiveLineMaterial(): LineBasicMaterial {
  return new LineBasicMaterial({ transparent: true, blending: AdditiveBlending, depthWrite: false })
}

/** Additive lines add colour × opacity: the opacity is their brightness. */
function paintLines(material: LineBasicMaterial, colour: Color, brightness: number): void {
  material.color.copy(colour)
  material.opacity = brightness
}
